import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Trash2, ArrowLeft, Eye, Upload } from "lucide-react";
import { QuestionGroupDialog } from "./QuestionGroupDialog";
import { QuestionDialog } from "./QuestionDialog";
import { ImportQuestionsDialog } from "./ImportQuestionsDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Tables } from "@/integrations/supabase/types";

type QuestionGroup = Tables<"question_groups"> & {
  questions?: { count: number }[];
  sort_order?: number;
};
type Question = Tables<"questions"> & {
  question_groups?: { name: string } | null;
  is_enabled?: boolean;
};
type JobPosition = { id: string; role_name: string };

interface QuestionGroupsTabProps {
  questionGroups: QuestionGroup[];
  questions: Question[];
  loading: boolean;
  onRefetch: () => void;
  canDelete?: boolean;
}

export function QuestionGroupsTab({
  questionGroups,
  questions,
  loading,
  onRefetch,
  canDelete = true,
}: QuestionGroupsTabProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<QuestionGroup | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<QuestionGroup | null>(
    null
  );
  const [questionTypeFilter, setQuestionTypeFilter] = useState<string>("all");
  const [mustHaveOnly, setMustHaveOnly] = useState<boolean>(false);
  const { toast } = useToast();

  // Fetch questions for selected group
  const { data: groupQuestions = [], refetch: refetchGroupQuestions } =
    useQuery({
      queryKey: ["group-questions", selectedGroup?.id],
      queryFn: async () => {
        if (!selectedGroup) return [];

        const { data, error } = await supabase
          .from("questions")
          .select(
            ` 
          *,
          question_groups:group_id (name)
        `
          )
          .eq("group_id", selectedGroup.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        return data as Question[];
      },
      enabled: !!selectedGroup,
    });

  const filteredQuestions = groupQuestions
    .filter(
      (q) =>
        questionTypeFilter === "all" || q.question_type === questionTypeFilter
    )
    .filter((q) => (mustHaveOnly ? q.is_must_have : true));
  // Fetch job positions to display names instead of IDs
  const { data: jobPositions = [] } = useQuery({
    queryKey: ["job-positions", "active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_positions")
        .select("id, role_name")
        .eq("is_active", true);
      if (error) throw error;
      return data || [];
    },
  });

  const jobPositionMap = jobPositions.reduce<Record<string, string>>(
    (acc, position) => {
      acc[position.id] = position.role_name;
      return acc;
    },
    {}
  );

  const groupCountMap = questions.reduce<
    Record<string, { total: number; mustHave: number }>
  >((acc, q) => {
    if (!q.group_id) return acc;
    if (q.is_enabled === false) return acc;
    if (!acc[q.group_id]) acc[q.group_id] = { total: 0, mustHave: 0 };
    acc[q.group_id].total += 1;
    if (q.is_must_have) acc[q.group_id].mustHave += 1;
    return acc;
  }, {});

  const handleDelete = async (id: string) => {
    if (!canDelete) return;
    if (!confirm("Are you sure you want to delete this question group?"))
      return;

    const { error } = await supabase
      .from("question_groups")
      .delete()
      .eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete question group",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Question group deleted successfully",
      });
      onRefetch();
    }
  };

  const handleDeleteQuestion = async (id: string) => {
    if (!canDelete) return;
    if (!confirm("Are you sure you want to delete this question?")) return;

    const { error } = await supabase.from("questions").delete().eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete question",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Question deleted successfully",
      });
      refetchGroupQuestions();
    }
  };

  const handleEdit = (group: QuestionGroup) => {
    setEditingGroup(group);
    setDialogOpen(true);
  };

  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setQuestionDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingGroup(null);
    setDialogOpen(true);
  };

  const handleCreateQuestion = () => {
    setEditingQuestion(null);
    setQuestionDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingGroup(null);
  };

  const handleQuestionDialogClose = () => {
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
  };

  const handleImportQuestions = () => {
    setImportDialogOpen(true);
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
  };

  const handleViewQuestions = (group: QuestionGroup) => {
    setSelectedGroup(group);
  };

  const handleBackToGroups = () => {
    setSelectedGroup(null);
  };

  if (loading) {
    return <div>Loading question groups...</div>;
  }

  // Show questions for selected group
  if (selectedGroup) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleBackToGroups}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Groups
          </Button>
          <div>
            <h2 className="text-xl font-semibold">
              Questions in "{selectedGroup.name}"
            </h2>
            <p className="text-muted-foreground">
              {groupQuestions.length} questions
            </p>
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={handleImportQuestions}>
              <Upload className="mr-2 h-4 w-4" />
              Import
            </Button>
            <Button onClick={handleCreateQuestion}>
              <Plus className="mr-2 h-4 w-4" />
              Add Question to Group
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Filter by type:</label>
          <select
            className="border rounded p-1 text-sm"
            value={questionTypeFilter}
            onChange={(e) => setQuestionTypeFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="mcq">MCQ</option>
            <option value="open_ended">Open Ended</option>
          </select>

          <div className="flex items-center gap-1 ml-4">
            <Checkbox
              id="mustHaveOnly"
              checked={mustHaveOnly}
              onCheckedChange={(v) => setMustHaveOnly(!!v)}
            />
            <label htmlFor="mustHaveOnly" className="text-sm">
              Must-have only
            </label>
          </div>
        </div>

        {groupQuestions.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-muted-foreground">
                  No questions in this group
                </p>
                <Button onClick={handleCreateQuestion} className="mt-4">
                  Create first question for this group
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredQuestions.map((question) => (
              <Card key={question.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge variant="secondary">
                          {question.question_type.toUpperCase()}
                        </Badge>
                        {question.is_must_have && (
                          <Badge variant="default" className="bg-yellow-500">
                            Must-have
                          </Badge>
                        )}

                        <Badge variant="outline">Score: {question.score}</Badge>
                      </div>
                      <CardTitle className="text-lg leading-relaxed">
                        <div className="prose dark:prose-invert max-w-none question-markdown-body">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {question.question_text}
                          </ReactMarkdown>
                        </div>
                      </CardTitle>
                      {question.job_position_ids &&
                        question.job_position_ids.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {question.job_position_ids.map((positionId) => {
                              const position = jobPositions.find(
                                (p) => p.id === positionId
                              );
                              return position ? (
                                <Badge key={positionId} variant="outline">
                                  Position: {position.role_name}
                                </Badge>
                              ) : null;
                            })}
                          </div>
                        )}
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditQuestion(question)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteQuestion(question.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {question.question_type === "mcq" && question.options && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Options:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(
                          question.options as Record<string, string>
                        ).map(([key, value]) => (
                          <div
                            key={key}
                            className={`p-2 rounded text-sm border ${
                              question.correct_answer === key
                                ? "bg-green-50 border-green-200"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <span className="font-medium">{key}:</span>
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {value}
                              </ReactMarkdown>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {question.question_type === "open_ended" &&
                    question.ai_evaluation_criteria && (
                      <div>
                        <p className="text-sm font-medium mb-2">
                          AI Evaluation Criteria:
                        </p>
                        <div className="text-sm text-muted-foreground">
                          <div className="prose prose-sm dark:prose-invert max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {question.ai_evaluation_criteria}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <QuestionDialog
          open={questionDialogOpen}
          onClose={handleQuestionDialogClose}
          editingQuestion={editingQuestion}
          questionGroups={[selectedGroup]}
          jobPositions={jobPositions}
          onSuccess={() => {
            refetchGroupQuestions();
            handleQuestionDialogClose();
          }}
        />

        <ImportQuestionsDialog
          open={importDialogOpen}
          onClose={handleImportDialogClose}
          selectedGroupId={selectedGroup.id}
          onSuccess={() => {
            refetchGroupQuestions();
            handleImportDialogClose();
          }}
        />
      </div>
    );
  }

  // Show question groups
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Question Groups</h2>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Group
        </Button>
      </div>

      {questionGroups.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-muted-foreground">No question groups found</p>
              <Button onClick={handleCreate} className="mt-4">
                Create your first question group
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {questionGroups.map((group) => (
            <Card key={group.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">
                      {group.sort_order ? `${group.sort_order}. ` : ""}
                      {group.name}
                    </CardTitle>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewQuestions(group)}
                      title="View questions"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(group)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(group.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {group.description && (
                  <CardDescription className="mb-3">
                    {group.description}
                  </CardDescription>
                )}
                <div className="my-4">
                  {group.job_position_ids &&
                  group.job_position_ids.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {group.job_position_ids.map((positionId) => (
                        <Badge
                          key={positionId}
                          variant="secondary"
                          className="text-xs"
                        >
                          {jobPositionMap[positionId] || positionId}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-xs">
                      No Position Defined
                    </Badge>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Enabled questions:</span>
                    {(() => {
                      const total = groupCountMap[group.id]?.total ?? 0;
                      const mismatch = total < group.questions_per_test;
                      return (
                        <span
                          className={cn(
                            "font-medium",
                            mismatch && "text-red-500"
                          )}
                        >
                          {total} / {group.questions_per_test}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="flex justify-between">
                    <span>Enabled must-have questions:</span>
                    {(() => {
                      const must = groupCountMap[group.id]?.mustHave ?? 0;
                      const mismatch = must < group.must_have_questions;
                      return (
                        <span
                          className={cn(
                            "font-medium",
                            mismatch && "text-red-500"
                          )}
                        >
                          {must} / {group.must_have_questions}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <QuestionGroupDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        editingGroup={editingGroup}
        onSuccess={() => {
          onRefetch();
          handleDialogClose();
        }}
      />
    </div>
  );
}
